import React, { FC, useEffect, useMemo } from "react";
import {
  fetchedPlayer,
  fetchAccessibleBoards,
  accessibleBoardsByDate,
  hasCurrentActions,
  saveRegistrations,
  hasRegistrations,
  prices,
  getPlayerLoading,
  getPaymentLoading,
  createPayment,
  fetchPlayerFromLicence,
  cleanState,
  fetchedNoPlayer,
  fetchPlayerById,
  getPlayersWithRegistrations,
  syncSelectedBoardsWithParticipations,
} from "../../features/player/playerSlice";
import { getTournoi } from "../../features/tournoi/tournoiSlice";
import { admin } from "../../features/session/sessionSlice";

import { useNavigate, useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../store";
import { RegisterListBoardsDate } from "./RegisterListBoardsDate";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Stack,
  Zoom,
} from "@mui/material";

interface RegisterSelectProps {}

const RegisterSelect: FC<RegisterSelectProps> = () => {
  const player = useAppSelector(fetchedPlayer);
  const accessibleBoardByDate = useAppSelector(accessibleBoardsByDate);
  const hasCurrentActionsState = useAppSelector(hasCurrentActions);
  const pricesState = useAppSelector(prices);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const tournoi = useAppSelector(getTournoi);
  const isAdmin = useAppSelector(admin);
  const playerHasRegistrations = useAppSelector(hasRegistrations);
  const loading = useAppSelector(getPlayerLoading);
  const paymentLoading = useAppSelector(getPaymentLoading);
  const [searchParams] = useSearchParams();
  const playerIdFromUrl = searchParams.get("playerId");
  const licenceIdFromUrl = searchParams.get("licenceId");
  const noPlayer = useAppSelector(fetchedNoPlayer);
  const playersWithRegistrationsRaw = useAppSelector(
    getPlayersWithRegistrations
  );

  // Mémoriser playersWithRegistrations pour éviter les re-rendus constants
  const playersWithRegistrations = useMemo(() => {
    return playersWithRegistrationsRaw;
  }, [playersWithRegistrationsRaw]);

  useEffect(() => {
    if (playerIdFromUrl) {
      const localPlayer = playersWithRegistrations.find(
        (p) => p.id === playerIdFromUrl
      );
      if (localPlayer && (!player || player.id !== playerIdFromUrl)) {
        dispatch({
          type: "player/fetchPlayerById/fulfilled",
          payload: localPlayer,
        });
      } else if (!localPlayer) {
        dispatch(cleanState());
        dispatch(fetchPlayerById(playerIdFromUrl));
      }
    } else if (
      licenceIdFromUrl &&
      (!player || player.licenceId !== licenceIdFromUrl)
    ) {
      dispatch(cleanState());
      dispatch(fetchPlayerFromLicence(licenceIdFromUrl));
    }
    if (!player && !loading && !playerIdFromUrl && !licenceIdFromUrl) {
      navigate("/register");
    }
  }, [
    dispatch,
    navigate,
    player,
    playerIdFromUrl,
    licenceIdFromUrl,
    loading,
    playersWithRegistrations,
  ]);

  useEffect(() => {
    if (player?.id) {
      dispatch(fetchAccessibleBoards(player.id));
    }
  }, [dispatch, player]);

  // Synchroniser selectedBoardIds avec les vraies participations après chargement du joueur
  useEffect(() => {
    if (player && player.participation) {
      dispatch(syncSelectedBoardsWithParticipations());
    }
  }, [dispatch, player]);

  if (!loading && playerIdFromUrl && noPlayer) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Alert severity="error">
          Aucun joueur trouvé pour cet identifiant.
        </Alert>
      </Box>
    );
  }

  if (loading) {
    return (
      <Grid
        container
        alignItems="center"
        justifyContent="center"
        style={{ minHeight: "100vh" }}
      >
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Zoom in>
      <Box width="100%" maxWidth={800} mx="auto">
        <Stack spacing={4} alignItems="center">
          {!isAdmin && playerHasRegistrations && (
            <Alert
              severity="warning"
              variant="outlined"
              sx={{
                borderColor: "warning.main",
                bgcolor: "warning.light",
                color: "warning.contrastText",
                fontWeight: 500,
                width: "100%",
                maxWidth: 800,
              }}
            >
              Vous êtes déjà inscrit à au moins un tableau. Pour toute
              modification, contactez-nous par mail à l'adresse{" "}
              <a
                href={`mailto:${tournoi?.mail}`}
                style={{ color: "inherit", textDecoration: "underline" }}
              >
                {tournoi?.mail}
              </a>
            </Alert>
          )}

          {Object.keys(accessibleBoardByDate)
            .sort()
            .map((date) => (
              <RegisterListBoardsDate
                key={date}
                date={date}
                accessibleBoards={accessibleBoardByDate[date]}
              />
            ))}

          {isAdmin ? (
            <Box sx={{ width: "98%" }}>
              <Stack
                spacing={3}
                alignItems="center"
                justifyContent="center"
                direction="row"
              >
                <Chip
                  label={`Déjà payé ${pricesState.formerPrice} €`}
                  color="primary"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
                <Button
                  variant="contained"
                  disabled={!hasCurrentActionsState}
                  onClick={async () => {
                    const resultAction = await dispatch(
                      saveRegistrations("SAVE")
                    );
                    if (saveRegistrations.fulfilled.match(resultAction)) {
                      await dispatch(fetchAccessibleBoards(player?.id));
                      await dispatch(
                        fetchPlayerFromLicence(player?.licenceId!)
                      );
                      navigate("/register/validation");
                    }
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    Sauvegarder les inscriptions {pricesState.priceDifference} €
                  </Box>
                </Button>
                <Chip
                  label={`Nouveau prix ${pricesState.newPrice} €`}
                  color="success"
                  variant="outlined"
                  sx={{ fontWeight: 600 }}
                />
              </Stack>
            </Box>
          ) : (
            <>
              <Alert
                severity="warning"
                variant="standard"
                icon={false}
                sx={{
                  border: "1px solid",
                  borderColor: "warning.main",
                  fontWeight: 500,
                }}
              >
                Attention, le remboursement n'est possible que sur présentation
                d'un certificat médical.
              </Alert>
              {!(!isAdmin && playerHasRegistrations) && (
                <Button
                  variant="contained"
                  disabled={!hasCurrentActionsState || paymentLoading}
                  onClick={async () => {
                    await dispatch(createPayment("SAVE"));
                  }}
                  sx={{
                    px: 4,
                    py: 1.5,
                    textTransform: "none",
                    fontWeight: 600,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    Payer l'inscription {pricesState.newPrice} €
                    {paymentLoading && (
                      <CircularProgress
                        color="info"
                        size={24}
                        sx={{ marginLeft: 1 }}
                      />
                    )}
                  </Box>
                </Button>
              )}
            </>
          )}
        </Stack>
      </Box>
    </Zoom>
  );
};

export default RegisterSelect;
